// components/PromptComposer.jsx — the app's generate box.
//
// Lifted verbatim out of WorkspacePage so the video player can carry the same bar instead of
// a lookalike. What lives here is everything VISUAL and everything self-contained: the card,
// the textarea, the √x symbol palette, the mode dropdown, the send button, and the two
// outside-click handlers those popovers need.
//
// What deliberately does NOT live here is where the bar sits and what pressing send does.
// Workspace docks it to the bottom of the viewport through a portal and routes handoff modes
// into CreateProjectModal; the video page drops it inline under the player and generates a
// follow-up. Pulling that in would mean one component with two personalities, so the caller
// keeps it: this one owns the box, the caller owns the consequences.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, ChevronDown, Lock } from "lucide-react";
import { COMPOSER_MODES as MODES, SYMBOL_GROUPS } from "./composerConfig";
import { useT } from "../i18n/useT";

// Keep in step with MAX_PROMPT_CHARS in src/backend/api/models.py — the server rejects
// anything longer, and test_prompt_length_limits.py fails if the two drift apart.
export const MAX_PROMPT_CHARS = 5000;

const PromptComposer = ({
  value,
  onChange,
  onSubmit,
  /** Chosen mode VALUE. Controlled by the caller so it can persist the choice. */
  mode,
  onModeChange,
  /** Modes flagged subscriberOnly send here instead of being selected. */
  onLockedMode,
  /** Fires when a "handoff" mode (Homework/Lecture/Project) is picked — those are LAUNCHERS,
   *  not modes, and the caller opens the relevant flow. Omit and they are hidden entirely. */
  onHandoff,
  /** Fires with a File[] when a document is dropped ON THE BAR. Same split as onHandoff: this
   *  component owns the box and the drag affordance, the caller owns what a drop MEANS — Home
   *  ships the file to the Workspace, Workspace opens the chooser in place. Omit and the bar
   *  ignores drops entirely, which is what the video page wants. */
  onFilesDropped,
  isSubscriber = false,
  sending = false,
  placeholder,
  rows = 2,
  /** Escape hatch for the caller's own layout concerns (Workspace sets viewTransitionName). */
  cardStyle,
  textSize = "text-[19px]",
  /* "stacked" (default) puts the controls on their own row under the field — right for the
     Workspace dock, which is capped at max-w-3xl and is the page's primary action.
     "row" puts everything on one line. Under the video the composer inherits the player's
     full width, and at ~1050px the stacked shape left the √x and mode picker huddled at the
     bottom-left with the send button ~870px away at the bottom-right and nothing in between —
     a tall box mostly made of void. One row removes the void and halves the height. */
  layout = "stacked",
  /* The Workspace dock is the hero of its page and earns a 2px black edge. Under a video it
     out-weighs everything on screen including the player, so the eye lands on an empty box
     instead of the thing being watched. */
  borderClass = "border-2 border-black",
  className = "",
}) => {
  const row = layout === "row";
  const t = useT();
  const [modeOpen, setModeOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // A COUNTER, not a boolean. dragenter/dragleave fire for every descendant the cursor crosses,
  // so moving over the textarea inside the card raises leave-then-enter and a boolean flickers
  // the highlight off and on the whole way in. Counting enters and leaves means the state only
  // clears when the pointer has actually left the card.
  const dragDepth = useRef(0);
  const [symbolsOpen, setSymbolsOpen] = useState(false);
  const promptRef = useRef(null);
  const symbolsRef = useRef(null);
  const symbolsBtnRef = useRef(null);
  const modeRef = useRef(null);

  // Validated against MODES rather than trusted: an unknown value would leave activeMode
  // undefined and take the whole bar down on the first render.
  const activeMode = MODES.find((m) => m.value === mode) || MODES[0];
  const isHandoff = activeMode.input === "handoff";
  const visibleModes = onHandoff ? MODES : MODES.filter((m) => m.input !== "handoff");

  useEffect(() => {
    if (!modeOpen) return;
    const onDown = (e) => {
      if (modeRef.current && !modeRef.current.contains(e.target)) setModeOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [modeOpen]);

  useEffect(() => {
    if (!symbolsOpen) return;
    const onDown = (e) => {
      // The BUTTON is excluded as well as the bar. Without that, its own click closes the
      // palette here and the button's onClick immediately reopens it, so it never shuts.
      const inBar = symbolsRef.current?.contains(e.target);
      const inBtn = symbolsBtnRef.current?.contains(e.target);
      if (!inBar && !inBtn) setSymbolsOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setSymbolsOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [symbolsOpen]);

  // What the chooser's own uploader accepts. Anything else is ignored rather than rejected
  // loudly: a stray drag of a .docx onto the bar is a misfire, not an error worth a red line.
  const ACCEPTS = /\.(pdf|png|jpe?g|heic)$/i;

  const dragHasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");

  const onDragEnter = useCallback((e) => {
    if (!onFilesDropped || !dragHasFiles(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragOver(true);
  }, [onFilesDropped]);

  const onDragOver = useCallback((e) => {
    if (!onFilesDropped || !dragHasFiles(e)) return;
    // Required on EVERY dragover, not just once: without it the browser treats the element as
    // an invalid target, shows the "no drop" cursor and then navigates away to the file on
    // release — which looks exactly like the app crashing.
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, [onFilesDropped]);

  const onDragLeave = useCallback((e) => {
    if (!onFilesDropped || !dragHasFiles(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  }, [onFilesDropped]);

  const onDrop = useCallback((e) => {
    if (!onFilesDropped || !dragHasFiles(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []).filter((f) => ACCEPTS.test(f.name));
    if (files.length) onFilesDropped(files);
  }, [onFilesDropped]);

  // Insert at the caret, not at the end — someone fixing a symbol mid-expression should not
  // have it land after everything they typed.
  const insertSymbol = useCallback(
    (sym) => {
      const el = promptRef.current;
      if (!el) {
        onChange(`${value}${sym}`);
        return;
      }
      const start = el.selectionStart ?? value.length;
      const end = el.selectionEnd ?? value.length;
      const next = value.slice(0, start) + sym + value.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        el.focus();
        const caret = start + sym.length;
        el.setSelectionRange(caret, caret);
      });
    },
    [value, onChange],
  );

  return (
    <div className={className}>
      {/* Floating symbol bar. Sits ABOVE the composer as its own pill rather than as a
          dropdown inside it: the composer is only 3 lines tall, so a panel anchored within it
          either overflows the card or shoves the controls down. Scrolls horizontally, so
          adding symbols never changes the bar's height.

          It used to mount and unmount on `symbolsOpen`, so the bar appeared at full height in
          one frame and the composer under it jumped by exactly that much. The snap is what
          reads as jagged — not the speed of anything. The bar now STAYS MOUNTED and the grid
          track it sits in animates instead, so the space opens over 220ms and everything
          below slides. 0fr -> 1fr rather than a max-height guess, because the height is
          content-derived and a max-height safe enough to never clip would spend most of the
          duration animating nothing. Same treatment as the Home composer. */}
      <div
        className="grid transition-all duration-[220ms] ease-out motion-reduce:transition-none"
        style={{
          gridTemplateRows: symbolsOpen ? "1fr" : "0fr",
          opacity: symbolsOpen ? 1 : 0,
        }}
        aria-hidden={!symbolsOpen}
      >
        {/* The clipper. The gap to the composer lives INSIDE it so it collapses with the bar;
            on the outer grid it would leave a permanent 8px hole when closed. */}
        <div className="overflow-hidden min-h-0">
          <div
            ref={symbolsRef}
            className={`mb-2 rounded-2xl bg-white border border-gray-200 px-2 py-2 overflow-x-auto ${
              symbolsOpen ? "" : "pointer-events-none"
            }`}
            style={{
              boxShadow: "0 8px 26px rgba(15,23,42,0.13), 0 2px 6px rgba(15,23,42,0.07)",
              scrollbarWidth: "none",
            }}
          >
            <div className="flex items-center gap-3 w-max">
              {SYMBOL_GROUPS.map((g, gi) => (
                <React.Fragment key={g.label}>
                  {gi > 0 && <span className="w-px h-7 bg-gray-200 flex-shrink-0" />}
                  <div className="flex items-center gap-0.5">
                    {g.items.map((sym) => (
                      <button
                        key={sym}
                        type="button"
                        onClick={() => insertSymbol(sym)}
                        title={g.label}
                        /* Kept out of the tab order while collapsed: the bar stays mounted so
                           it can animate, and 60 invisible buttons in the tab order is worse
                           than the jitter this replaced. */
                        tabIndex={symbolsOpen ? 0 : -1}
                        className="w-9 h-9 rounded-lg text-[16px] text-gray-800 hover:bg-gray-100 active:bg-gray-200 transition-colors flex-shrink-0"
                        style={{ fontFamily: "'Times New Roman', serif" }}
                      >
                        {sym}
                      </button>
                    ))}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        // Shadow inline, not an arbitrary Tailwind class: commas inside rgba() break its
        // arbitrary-value parser and it silently emits a transparent shadow.
        style={{
          boxShadow: "0 10px 34px rgba(15,23,42,0.14), 0 2px 6px rgba(15,23,42,0.07)",
          ...cardStyle,
        }}
        /* THE DRAG STATE CHANGES THE BORDER AND THE GROUND, NOT THE GEOMETRY. Anything that
           alters size or position — a dashed outline that adds width, padding, a scale — makes
           the target move while the pointer is over it, and a drop target that flinches is
           harder to hit than one that does not react at all. Colour is free: the border swaps
           to Magi blue and the card takes a pale blue fill, both solid, and the shadow, radius
           and padding are untouched.
           `relative` so the overlay below can position against this card. */
        className={`relative rounded-2xl transition-colors ${
          dragOver ? "bg-[#EAF3FF] border-2 border-[#0076E5]" : `bg-white ${borderClass}`
        } ${row ? "px-3 py-2 flex items-center gap-2" : "p-3"}`}
      >
        {/* The label sits over the card rather than in the layout, so it cannot push the
            textarea around at the exact moment someone is aiming at it. pointer-events-none is
            load-bearing: an element under the cursor that is not the drop target would swallow
            dragleave and strand the highlight on. */}
        {dragOver && (
          <div className="absolute inset-0 z-10 rounded-2xl flex items-center justify-center
            pointer-events-none bg-[#EAF3FF]">
            <span className="text-[#0076E5] text-[15px] font-bold">
              {t('composer_drop_hint')}
            </span>
          </div>
        )}
        <textarea
          ref={promptRef}
          value={value}
          // Matches MAX_PROMPT_CHARS in backend/api/models.py. Without it the box accepted
          // any length, and a paste over the server limit queued a job, spent a credit and
          // failed a minute later on a validation error the user could do nothing with.
          // The browser now simply stops the paste at the limit.
          maxLength={MAX_PROMPT_CHARS}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            // Stopped here as well as handled: on the video page this box sits inside the
            // player's hover area, where a bare space would otherwise toggle playback.
            e.stopPropagation();
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSubmit();
            }
          }}
          rows={row ? 1 : rows}
          placeholder={placeholder}
          className={`w-full bg-transparent text-gray-900 placeholder-gray-600 ${textSize} resize-none outline-none ${
            /* In a row the field is a flex child and must be allowed to shrink: min-w-0,
               or a long prompt pushes the controls off the right edge of the card. */
            row ? "flex-1 min-w-0 px-1 py-1.5 leading-6" : "px-2 pt-2.5 pb-3"
          }`}
        />
        <div className={`flex items-center gap-2 ${row ? "flex-shrink-0" : "px-1"}`}>
          <button
            ref={symbolsBtnRef}
            onClick={() => {
              setSymbolsOpen((o) => !o);
              setModeOpen(false);
            }}
            aria-label="Insert a math symbol"
            aria-expanded={symbolsOpen}
            className={`flex items-center justify-center w-9 h-9 rounded-full border text-[17px] transition-colors ${
              symbolsOpen
                ? "bg-gray-900 text-white border-gray-900"
                : "bg-white text-gray-700 border-gray-300/80 hover:bg-gray-50"
            }`}
          >
            <span className="leading-none" style={{ fontFamily: "'Times New Roman', serif" }}>
              &#8730;x
            </span>
          </button>

          <div className="relative" ref={modeRef}>
            <button
              onClick={() => setModeOpen((o) => !o)}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-gray-300/80 text-[15px] text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {activeMode.label}
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {modeOpen && (
              <div className="absolute bottom-full mb-2 left-0 min-w-[180px] rounded-xl overflow-hidden py-1 bg-white border border-gray-200 shadow-lg z-10">
                {visibleModes.map((m) => {
                  const locked = m.subscriberOnly && !isSubscriber;
                  return (
                    <button
                      key={m.value}
                      onClick={() => {
                        setModeOpen(false);
                        // A locked pick must not silently select a mode the server will
                        // downgrade — that reads as "HD is broken" rather than "HD is paid".
                        if (locked) {
                          onLockedMode?.();
                          return;
                        }
                        if (m.input === "handoff") {
                          onHandoff?.(m);
                          return;
                        }
                        onModeChange?.(m.value);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                        locked
                          ? "text-gray-400"
                          : m.value === mode
                            ? "text-gray-900 font-medium"
                            : "text-gray-500"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        {m.Icon && <m.Icon className="w-3.5 h-3.5" />}
                        {m.label}
                        {locked && <Lock className="w-3 h-3 text-gray-300 flex-shrink-0" />}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button
            onClick={onSubmit}
            disabled={sending || (!isHandoff && !value.trim())}
            aria-label={isHandoff ? activeMode.label : t("ws_generate")}
            className={`${row ? "" : "ml-auto"} w-9 h-9 rounded-full bg-[#635BFF] hover:bg-[#5851e0] disabled:bg-white disabled:text-gray-400 text-white flex items-center justify-center transition-colors flex-shrink-0`}
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptComposer;
